import React, { FC, useState, useEffect, useRef, useCallback } from "react";
import { AddressSuggestion } from "../../../../../../infrastructure/http/modules/adressevaelger/models";
import { addressFetch, addressDetailsFetch } from "../../../../../../infrastructure/http/modules/adressevaelger/fetch";
import { Address } from "../../../../../../infrastructure/http/modules/cart/cart.models";
import useDebounce from '../../../../../common/useDebounce';
import { EventKeysNames } from "../../../../../../infrastructure/const";

type AdressevaelgerProps = {
    label: string,
    addressSearchText: string,
    onSelect: (address: Address) => void;
};

const AdressevaelgerAutocomplete: FC<AdressevaelgerProps> = ({
  label,
  addressSearchText,
  onSelect,
}) => {

    const elRef = useRef<HTMLDivElement>(null);
    const minCharacters = 2;
    const addressTypeName = "adresse";
    //This regions const will be changed
    const regions: Record<string, string> = {
        "0101": "Hovedstaden",
        "0751": "Midtjylland",
        "0851": "Nordjylland",
        "0461": "Syddanmark",
        "0561": "Syddanmark",
        "0630": "Syddanmark",
        "0147": "Hovedstaden",
        "0730": "Midtjylland",
        "0791": "Midtjylland",
        "0621": "Syddanmark",
        "0740": "Midtjylland",
        "0657": "Midtjylland",
        "0615": "Midtjylland",
        "0265": "Sjælland",
        "0370": "Sjælland",
        "0330": "Sjælland",
        "0540": "Syddanmark",
        "0157": "Hovedstaden",
        "0316": "Sjælland",
        "0860": "Nordjylland",
        "0159": "Hovedstaden",
        "0376": "Sjælland",
        "0217": "Hovedstaden",
        "0813": "Nordjylland",
        "0580": "Syddanmark",
        "0479": "Syddanmark",
        "0746": "Midtjylland",
        "0760": "Midtjylland",
        "0259": "Sjælland",
        "0661": "Midtjylland",
        "0510": "Syddanmark",
        "0230": "Hovedstaden",
        "0173": "Hovedstaden",
        "0430": "Syddanmark",
        "0167": "Hovedstaden",
        "0573": "Syddanmark",
        "0607": "Syddanmark",
        "0326": "Sjælland",
        "0219": "Hovedstaden",
        "0169": "Hovedstaden",
        "0151": "Hovedstaden",
        "0253": "Sjælland",
        "0779": "Midtjylland",
        "0710": "Midtjylland",
        "0766": "Midtjylland",
        "0390": "Sjælland",
        "0360": "Sjælland",
        "0787": "Nordjylland",
        "0250": "Hovedstaden",
        "0575": "Syddanmark",
        "0846": "Nordjylland",
        "0240": "Hovedstaden",
        "0706": "Midtjylland",
        "0420": "Syddanmark",
        "0400": "Hovedstaden",
        "0185": "Hovedstaden",
        "0756": "Midtjylland",
        "0270": "Hovedstaden",
        "0210": "Hovedstaden",
        "0550": "Syddanmark",
        "0849": "Nordjylland",
        "0190": "Hovedstaden",
        "0707": "Midtjylland",
        "0410": "Syddanmark",
        "0820": "Nordjylland",
        "0175": "Hovedstaden",
        "0810": "Nordjylland",
        "0320": "Sjælland",
        "0153": "Hovedstaden",
        "0329": "Sjælland",
        "0306": "Sjælland",
        "0450": "Syddanmark",
        "0260": "Hovedstaden",
        "0340": "Sjælland",
        "0480": "Syddanmark",
        "0840": "Nordjylland",
        "0165": "Hovedstaden",
        "0350": "Sjælland",
        "0163": "Hovedstaden",
        "0530": "Syddanmark",
        "0223": "Hovedstaden",
        "0201": "Hovedstaden",
        "0440": "Syddanmark",
        "0671": "Midtjylland",
        "0336": "Sjælland",
        "0727": "Midtjylland",
        "0161": "Hovedstaden",
        "0773": "Nordjylland",
        "0665": "Midtjylland",
        "0269": "Sjælland",
        "0183": "Hovedstaden",
        "0187": "Hovedstaden",
        "0155": "Hovedstaden",
        "0482": "Syddanmark",
        "0492": "Syddanmark",
        "0741": "Midtjylland",
        "0563": "Syddanmark",
        "0825": "Nordjylland"
    };

    const [addressSearchInput, setAddressSearchInput] = useState<string>(addressSearchText);
    const [addressSearchLastSelect, setAddressSearchLastSelect] = useState<string>(addressSearchText);
    const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
    const [suggestionIndex, setSuggestionIndex] = useState<number>(-1);
    const debouncedAddressSearchInput = useDebounce(addressSearchInput, 300);


    const handleClickOutside = useCallback(
        (evt: MouseEvent) => {
            if (elRef.current && !elRef.current.contains(evt.target as Node)) {
                setAddressSearchInput(addressSearchLastSelect);
                setSuggestions([]);
                setSuggestionIndex(-1);  
            }
        },
        [addressSearchLastSelect, elRef]
    );

    useEffect(() => {
        document.addEventListener("click", handleClickOutside, true);
        
        return () => {
            document.removeEventListener("click", handleClickOutside, true);
        };
    }, [handleClickOutside]);
    

    const handleInputChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
        const { value } = event.currentTarget;    
        
        if (minCharacters <= value.length && addressSearchInput !== value) {
            setAddressSearchInput(value);
            setSuggestionIndex(-1);
        }
        else{
            onSelect(new Address());

            setAddressSearchInput(value);
            setAddressSearchLastSelect('');
            setSuggestions([]);
            setSuggestionIndex(-1);   
        }
    }

    const getSuggestions = useCallback(
        (value: string): void => {
            addressFetch(
                value,
                (suggestions: AddressSuggestion[]) => {
                    setSuggestions(suggestions);
                }
            )        
        },
        []
    );

    useEffect(() => {
        if (debouncedAddressSearchInput === addressSearchInput && addressSearchLastSelect !== addressSearchInput  && addressSearchInput.length >= minCharacters) {
            getSuggestions(addressSearchInput);
        }
    }, [getSuggestions, addressSearchInput, addressSearchLastSelect, debouncedAddressSearchInput]);


    const suggestionSelect = async (suggestion: AddressSuggestion) => {
        suggestionAddressSelect(suggestion);            
    }

    const suggestionAddressSelect = async (suggestion: AddressSuggestion) => {   

        if(!suggestion.husnummerId || suggestion.type !== addressTypeName) {
            suggestionAddressSearch(suggestion);
        }
        else {
            suggestionAddressSet(suggestion);
        }
    }

    const suggestionAddressSearch = async (suggestion: AddressSuggestion) => {   

        setAddressSearchInput(suggestion.titel);
        setSuggestions([]);
        setSuggestionIndex(-1); 
    }

    const suggestionAddressSet = async (suggestion: AddressSuggestion) => {   
        const addressDetails = await addressDetailsFetch(suggestion.id);
        if(addressDetails != null) {

            const addressLine = [];
            addressLine.push(addressDetails.husnummer.vejnavn);
            if(addressDetails.husnummer.husnummertekst) {
                addressLine.push(addressDetails.husnummer.husnummertekst)
            }

            const addressLine2 = [];
            if(addressDetails.etagebetegnelse) {
                addressLine2.push(addressDetails.etagebetegnelse)
            }
            if(addressDetails.doerbetegnelse) {
                addressLine2.push(addressDetails.doerbetegnelse)
            }
            
            let address = new Address();
            address.addressApiText = addressDetails.adressebetegnelse;
            address.addressLine1 = addressLine.join(' ');
            address.addressLine2 = addressLine2.join(' ');
            address.city = addressDetails.husnummer.postnummer.navn;
            address.postalCode = addressDetails.husnummer.postnummer.postnr;
            address.county = regions[addressDetails.husnummer.navngivenvejkommunedel.kommune];    

            onSelect(address);
            setAddressSearchInput(suggestion.titel);
            setAddressSearchLastSelect(suggestion.titel);
            setSuggestions([]);
            setSuggestionIndex(-1); 
        }
        else {
            onSelect(new Address());
            setAddressSearchInput('');
            setAddressSearchLastSelect('');
            setSuggestions([]);
            setSuggestionIndex(-1); 
        }
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if(suggestions.length === 0) {
            return;
        }

        switch(event.key){
            case EventKeysNames.Escape:
                setAddressSearchInput(addressSearchLastSelect)
                setSuggestions([]);
                setSuggestionIndex(-1);   
                break;
            case EventKeysNames.Enter:
                if(suggestionIndex >= 0 && suggestionIndex < suggestions.length) {
                    const suggestion = suggestions[suggestionIndex];
                    suggestionSelect(suggestion);
                }
                break;
            case EventKeysNames.ArrowUp:
                setSuggestionIndex(suggestionIndex <= 0 ? suggestions.length - 1 : suggestionIndex - 1);  
                break;
            case EventKeysNames.ArrowDown:
                setSuggestionIndex(suggestionIndex < suggestions.length - 1 ? suggestionIndex + 1 : 0); 
                break;
        }
    }

    return <div ref={elRef}>
        <label className="required">{label}<span className="required"> *</span></label>
        <input type="text" value={addressSearchInput} onChange={handleInputChange} onKeyDown={handleKeyDown} />
        {suggestions.length > 0 &&
        <ul className="adressevaelger-autocomplete">
            {suggestions.map((suggestion, index) => (
                <li key={suggestion.id} 
                    className={index === suggestionIndex? "selected" : ""}
                    onClick={() => { suggestionSelect(suggestion); }}>
                        {suggestion.titel}
                </li>
            ))}
        </ul>}
    </div>
};

export default AdressevaelgerAutocomplete;